/**
 * @fileoverview Main application component with routing and lazy loading.
 */

import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { SEOProvider } from '@/lib/_seo';

// Lazy load page components for code splitting
const BookingSection = lazy(() =>
  import('@/components/booking/BookingSection').then((m) => ({ default: m.BookingSection }))
);

const ManageBooking = lazy(() =>
  import('@/pages/ManageBooking').then((m) => ({ default: m.ManageBooking }))
);

const TermsOfService = lazy(() =>
  import('@/pages/TermsOfService').then((m) => ({ default: m.TermsOfService }))
);

const PrivacyPolicy = lazy(() =>
  import('@/pages/PrivacyPolicy').then((m) => ({ default: m.PrivacyPolicy }))
);

const Unsubscribe = lazy(() =>
  import('@/pages/Unsubscribe').then((m) => ({ default: m.Unsubscribe }))
);

const Subscribe = lazy(() =>
  import('@/pages/Subscribe').then((m) => ({ default: m.Subscribe }))
);

// Admin pages (lazy loaded)
const AdminLogin = lazy(() =>
  import('@/pages/admin').then((m) => ({ default: m.AdminLogin }))
);
const AdminLayout = lazy(() =>
  import('@/pages/admin').then((m) => ({ default: m.AdminLayout }))
);
const AdminDashboard = lazy(() =>
  import('@/pages/admin').then((m) => ({ default: m.AdminDashboard }))
);
const AdminBookings = lazy(() =>
  import('@/pages/admin').then((m) => ({ default: m.AdminBookings }))
);
const AdminTables = lazy(() =>
  import('@/pages/admin').then((m) => ({ default: m.AdminTables }))
);
const AdminSettings = lazy(() =>
  import('@/pages/admin').then((m) => ({ default: m.AdminSettings }))
);

/**
 * Loading fallback component
 */
function PageLoader() {
  return (
    <div
      className="flex flex-col items-center justify-center min-h-[50vh]"
      role="status"
      aria-label="Loading page"
    >
      <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      <p className="mt-4 text-sm text-muted-foreground">Loading...</p>
    </div>
  );
}

/**
 * Main application component.
 */
function App() {
  return (
    <SEOProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Layout />}>
            <Route
              index
              element={
                <Suspense fallback={<PageLoader />}>
                  <BookingSection />
                </Suspense>
              }
            />
            <Route
              path="booking"
              element={
                <Suspense fallback={<PageLoader />}>
                  <BookingSection />
                </Suspense>
              }
            />
            <Route
              path="manage"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ManageBooking />
                </Suspense>
              }
            />
            <Route
              path="terms"
              element={
                <Suspense fallback={<PageLoader />}>
                  <TermsOfService />
                </Suspense>
              }
            />
            <Route
              path="privacy"
              element={
                <Suspense fallback={<PageLoader />}>
                  <PrivacyPolicy />
                </Suspense>
              }
            />
            <Route
              path="unsubscribe"
              element={
                <Suspense fallback={<PageLoader />}>
                  <Unsubscribe />
                </Suspense>
              }
            />
            <Route
              path="subscribe"
              element={
                <Suspense fallback={<PageLoader />}>
                  <Subscribe />
                </Suspense>
              }
            />
          </Route>

          {/* Admin Routes */}
          <Route
            path="admin"
            element={
              <Suspense fallback={<PageLoader />}>
                <AdminLogin />
              </Suspense>
            }
          />
          <Route
            path="admin"
            element={
              <Suspense fallback={<PageLoader />}>
                <AdminLayout />
              </Suspense>
            }
          >
            <Route
              path="dashboard"
              element={
                <Suspense fallback={<PageLoader />}>
                  <AdminDashboard />
                </Suspense>
              }
            />
            <Route
              path="bookings"
              element={
                <Suspense fallback={<PageLoader />}>
                  <AdminBookings />
                </Suspense>
              }
            />
            <Route
              path="tables"
              element={
                <Suspense fallback={<PageLoader />}>
                  <AdminTables />
                </Suspense>
              }
            />
            <Route
              path="settings"
              element={
                <Suspense fallback={<PageLoader />}>
                  <AdminSettings />
                </Suspense>
              }
            />
          </Route>
        </Routes>
      </BrowserRouter>
    </SEOProvider>
  );
}

export default App;
